import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { EASE } from "@/lib/site";

export default function Lightbox({ images, index, onClose, onNavigate }) {
  const open = images.length > 0 && index !== null;

  const prev = useCallback(
    () => onNavigate((index - 1 + images.length) % images.length),
    [index, images.length, onNavigate]
  );
  const next = useCallback(
    () => onNavigate((index + 1) % images.length),
    [index, images.length, onNavigate]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && images.length > 1) prev();
      else if (e.key === "ArrowRight" && images.length > 1) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, prev, next, images.length]);

  useEffect(() => {
    if (!window.__lenis) return;
    if (open) window.__lenis.stop();
    else window.__lenis.start();
  }, [open]);

  const current = open ? images[index] : null;

  return (
    <AnimatePresence>
      {open && current && (
        <motion.div
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close gallery"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 border border-cream/25 text-cream flex items-center justify-center hover:bg-brass hover:text-ink hover:border-brass transition-colors duration-300 z-10"
          >
            <X size={20} />
          </button>

          {images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="Previous photo"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 border border-cream/25 text-cream flex items-center justify-center hover:bg-brass hover:text-ink hover:border-brass transition-colors duration-300 z-10"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="Next photo"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 border border-cream/25 text-cream flex items-center justify-center hover:bg-brass hover:text-ink hover:border-brass transition-colors duration-300 z-10"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.965 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-5xl w-auto max-h-full flex flex-col items-center"
          >
            <img
              src={current.src}
              alt={current.alt}
              data-testid="lightbox-image"
              className="max-h-[78vh] w-auto max-w-full object-contain shadow-2xl"
            />
            <figcaption className="mt-5 text-cream/60 text-[11px] tracking-widest2 uppercase text-center max-w-xl">
              {current.alt} · {index + 1} / {images.length}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
